/**
 * Learning Progress Utilities
 * 
 * Provides functions for managing daily challenges and learning streaks.
 * These are pure functions that can be easily tested.
 * 
 * @module learningProgressUtils
 */

import { storage } from './storage';
import { REWARD_POINTS } from '../constants/gameplay';

/**
 * Generate a daily challenge for the user
 * 
 * Challenges are cached per day. If a challenge already exists for today,
 * it returns the cached challenge. Otherwise, it generates a random challenge.
 * 
 * @returns {Object} Challenge object with id, title, description, reward, icon, and type
 * 
 * @example
 * const challenge = generateDailyChallenge();
 * // { id: 'perfect-score', title: 'Perfect Score Mission', ... }
 */
export const generateDailyChallenge = () => {
  const today = new Date().toDateString();
  const savedChallenge = storage.get(`daily-challenge-${today}`);
  
  if (savedChallenge) {
    return savedChallenge;
  }
  
  const challenges = [
    {
      id: 'perfect-score',
      title: 'Perfect Score Mission',
      description: 'Get 100% on any subject',
      reward: REWARD_POINTS.PERFECT_SCORE,
      icon: '⭐',
      type: 'perfect_score'
    },
    {
      id: 'streak-master',
      title: 'Streak Master',
      description: 'Complete 3 subjects in a row',
      reward: 75,
      icon: '🔥',
      type: 'streak'
    },
    {
      id: 'speed-demon',
      title: 'Speed Demon',
      description: 'Answer 10 questions in under 5 minutes',
      reward: 60,
      icon: '⚡',
      type: 'speed'
    },
    {
      id: 'explorer',
      title: 'Subject Explorer',
      description: 'Try 5 different subjects today',
      reward: 40,
      icon: '🗺️',
      type: 'exploration'
    },
    {
      id: 'confidence-builder',
      title: 'Confidence Builder',
      description: 'Improve confidence in your weakest subject',
      reward: 55,
      icon: '💪',
      type: 'confidence'
    }
  ];
  
  const randomChallenge = challenges[Math.floor(Math.random() * challenges.length)];
  storage.set(`daily-challenge-${today}`, randomChallenge);
  return randomChallenge;
};

/**
 * Calculate updated learning streak based on last learning date
 * 
 * @param {string} lastLearningDate - The last date the user learned (as Date.toDateString())
 * @param {number} currentStreak - Current streak count
 * 
 * @returns {Object} Object containing:
 *   - newStreak: Updated streak count
 *   - newLastDate: Updated last learning date (today's date string)
 *   - streakChanged: Boolean indicating if streak was updated
 * 
 * @example
 * const { newStreak, newLastDate, streakChanged } = updateLearningStreak('Mon Jan 01 2024', 5);
 */
export const updateLearningStreak = (lastLearningDate, currentStreak = 0) => {
  const today = new Date().toDateString();
  
  // Already counted today - no change
  if (lastLearningDate === today) {
    return {
      newStreak: currentStreak,
      newLastDate: today,
      streakChanged: false
    };
  }
  
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayString = yesterday.toDateString();
  
  let newStreak = currentStreak;
  let streakChanged = true;
  
  // Continue streak if last date was yesterday
  if (lastLearningDate === yesterdayString) {
    newStreak = currentStreak + 1;
  } 
  // Reset streak if gap is more than one day
  else if (lastLearningDate && lastLearningDate !== today) {
    newStreak = 1;
  }
  // Start new streak if no previous date
  else {
    newStreak = 1;
  }
  
  return {
    newStreak,
    newLastDate: today,
    streakChanged
  };
};

